import { apiRequest } from "@/shared/lib/apiClient";
import type { InsurerSummary } from "./types";

/** Fiche assureur : coordonnées, identifiants légaux, agrément, interlocuteur. */
export type InsurerDetails = {
  address: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  license_number: string | null;
  rccm: string | null;
  id_nat: string | null;
  nif: string | null;
  contact_name: string | null;
  contact_role: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  /** Contrat entre SafeCheck et l'assureur (AAAA-MM-JJ). */
  contract_start: string | null;
  contract_end: string | null;
  /** Délai de traitement convenu (jours) et sa répartition en % : Admin, médecin, Sinistre (10/50/40 par défaut). */
  processing_days: number | null;
  share_admin: number | null;
  share_audit: number | null;
  share_claims: number | null;
};

export type AdminInsurer = InsurerSummary &
  InsurerDetails & {
    status: "active" | "inactive";
    companies_count: number;
    network_count: number;
    grids_count: number;
    packages_count: number;
    /** Prestataires du réseau sans grille (la grille est obligatoire). */
    network_without_grid: number;
  };

/** Fiche entreprise cliente : secteur, coordonnées, interlocuteur. */
export type CompanyDetails = {
  email: string | null;
  city: string | null;
  sector: string | null;
  rccm: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
};

export type InsurerCompany = CompanyDetails & {
  id: number;
  name: string;
  address: string;
  phone: string;
  status: "active" | "inactive";
  /** null : entreprise du catalogue, rattachée à aucun assureur. */
  insurer_id: number | null;
  insurer_name: string | null;
  agents_count?: number;
  dependants_count?: number;
  /** Packages autorisés chez l'assureur actuel. */
  packages?: PackageRef[];
};

/** Grille négociée par l'assureur avec un prestataire de son réseau. */
export type NetworkGrid = {
  id: number;
  code: string;
  name: string;
  valid_from: string;
  valid_to: string | null;
  currency: string;
  status: string;
  acts_count: number;
  in_force: boolean;
};

export type NetworkHospital = {
  hospital_id: number;
  provider_id: number | null;
  name: string;
  type: string | null;
  city: string | null;
  status: string;
  /** Réseau vu par l'Admin : grilles de cet assureur chez ce prestataire. */
  grids?: NetworkGrid[];
  grid_in_force?: NetworkGrid | null;
  other_insurers_with_grid?: number;
};

export type PackageRef = { id: number; code: string; name: string };

/** Fiche entreprise : assureur actuel et historique des rattachements. */
export type CompanyProfile = InsurerCompany & {
  history: Array<{ from: string | null; to: string | null; at: string; by: string | null }>;
};

/** Catalogue commun : entreprises (un assureur à la fois) et prestataires (plusieurs réseaux). */
export type CatalogueCompany = InsurerCompany & { is_mine: boolean };

export type CatalogueHospital = {
  id: number;
  name: string;
  type: string | null;
  city: string | null;
  /** Assureurs ayant ce prestataire dans leur réseau (chacun avec sa propre grille). */
  insurers: string[];
  in_network: boolean;
};

export type Catalogue = { companies: CatalogueCompany[]; hospitals: CatalogueHospital[]; packages: PackageRef[] };

export type CatalogueImport = { hospital_ids?: number[] };

export type ScopeCompany = {
  id: number;
  name: string;
  insurer_id: number;
  agents_count: number;
  dependants_count: number;
};

export type ScopeHospital = {
  id: number;
  name: string;
  type: string | null;
  city: string | null;
};

export type InsurerPayload = { name: string; code: string; status?: "active" | "inactive" } & Partial<InsurerDetails>;

export type CompanyPayload = { name: string; address?: string; phone?: string } & Partial<CompanyDetails>;

/* Admin : assureurs */

export async function fetchAdminInsurers(): Promise<AdminInsurer[]> {
  const response = await apiRequest<{ insurers: AdminInsurer[] }>("/admin/insurers", { auth: true });
  return response.insurers;
}

export async function createAdminInsurer(payload: InsurerPayload & CatalogueImport): Promise<AdminInsurer> {
  const response = await apiRequest<{ insurer: AdminInsurer }>("/admin/insurers", {
    method: "POST",
    auth: true,
    body: payload,
  });
  return response.insurer;
}

export async function updateAdminInsurer(id: number, payload: InsurerPayload): Promise<AdminInsurer> {
  const response = await apiRequest<{ insurer: AdminInsurer }>(`/admin/insurers/${id}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
  return response.insurer;
}

/* Admin : entreprises clientes */

export async function fetchAdminCompanies(insurerId?: number): Promise<InsurerCompany[]> {
  const qs = insurerId ? `?insurer_id=${insurerId}` : "";
  const response = await apiRequest<{ companies: InsurerCompany[] }>(`/admin/companies${qs}`, { auth: true });
  return response.companies;
}

export async function fetchAdminCatalogue(insurerId?: number): Promise<Catalogue> {
  const qs = insurerId ? `?insurer_id=${insurerId}` : "";
  return apiRequest<Catalogue>(`/admin/catalogue${qs}`, { auth: true });
}

/** Rattache des entreprises du catalogue ; transfer = true pour celles déjà chez un autre assureur. */
export async function attachAdminInsurerCompanies(
  insurerId: number,
  companyIds: number[],
  packageIds: number[],
  transfer: boolean,
): Promise<void> {
  await apiRequest(`/admin/insurers/${insurerId}/companies`, {
    method: "POST",
    auth: true,
    body: { company_ids: companyIds, package_ids: packageIds, transfer },
  });
}

/** Packages autorisés d'une entreprise chez l'assureur (au moins un). */
export async function setAdminCompanyPackages(insurerId: number, companyId: number, packageIds: number[]): Promise<void> {
  await apiRequest(`/admin/insurers/${insurerId}/companies/${companyId}/packages`, {
    method: "PUT",
    auth: true,
    body: { package_ids: packageIds },
  });
}

export async function detachAdminInsurerCompany(insurerId: number, companyId: number): Promise<void> {
  await apiRequest(`/admin/insurers/${insurerId}/companies/${companyId}`, { method: "DELETE", auth: true });
}

export async function createAdminCompany(insurerId: number | null, payload: CompanyPayload): Promise<InsurerCompany> {
  const response = await apiRequest<{ company: InsurerCompany }>("/admin/companies", {
    method: "POST",
    auth: true,
    body: { ...payload, insurer_id: insurerId },
  });
  return response.company;
}

export async function fetchAdminCompany(id: number): Promise<CompanyProfile> {
  const response = await apiRequest<{ company: CompanyProfile }>(`/admin/companies/${id}`, { auth: true });
  return response.company;
}

export async function setAdminCompanyStatus(id: number, status: "active" | "inactive"): Promise<InsurerCompany> {
  const response = await apiRequest<{ company: InsurerCompany }>(`/admin/companies/${id}/status`, {
    method: "PUT",
    auth: true,
    body: { status },
  });
  return response.company;
}

export async function updateAdminCompany(id: number, payload: CompanyPayload): Promise<InsurerCompany> {
  const response = await apiRequest<{ company: InsurerCompany }>(`/admin/companies/${id}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
  return response.company;
}

/* Admin : réseau de prestataires */

export async function fetchAdminInsurerNetwork(insurerId: number): Promise<NetworkHospital[]> {
  const response = await apiRequest<{ hospitals: NetworkHospital[] }>(`/admin/insurers/${insurerId}/network`, {
    auth: true,
  });
  return response.hospitals;
}

export async function attachAdminInsurerHospitals(insurerId: number, hospitalIds: number[]): Promise<void> {
  await apiRequest(`/admin/insurers/${insurerId}/network`, {
    method: "POST",
    auth: true,
    body: { hospital_ids: hospitalIds },
  });
}

export async function detachAdminInsurerHospital(insurerId: number, hospitalId: number): Promise<void> {
  await apiRequest(`/admin/insurers/${insurerId}/network/${hospitalId}`, { method: "DELETE", auth: true });
}

/* Contexte de travail (tous comptes) */

export async function fetchScopeInsurers(): Promise<InsurerSummary[]> {
  const response = await apiRequest<{ insurers: InsurerSummary[] }>("/scope/insurers", { auth: true });
  return response.insurers;
}

export async function fetchScopeCompanies(): Promise<ScopeCompany[]> {
  const response = await apiRequest<{ companies: ScopeCompany[] }>("/scope/companies", { auth: true });
  return response.companies;
}

export async function fetchScopeHospitals(): Promise<ScopeHospital[]> {
  const response = await apiRequest<{ hospitals: ScopeHospital[] }>("/scope/hospitals", { auth: true });
  return response.hospitals;
}
